import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys';
import { sessionsApi } from './useSessionsApi';
import type { UserSessionDto } from './useSessionsApi';

/**
 * TanStack Query hook for fetching the current user's active sessions.
 * Returns the array sorted by `lastActiveAt` DESC (most recent first).
 *
 * @param enabled - Whether the query should automatically run (default: true)
 * @see HZVT-149_ADD_AUTH_SESSION_MANAGEMENT
 */
export function useSessionsQuery(enabled = true) {
  return useQuery<UserSessionDto[]>({
    queryKey: queryKeys.sessions.list(),
    queryFn: sessionsApi.list,
    enabled,
  });
}

/**
 * TanStack Query mutation hook for revoking a single session.
 * Invalidates the sessions list on success so the UI immediately reflects
 * the revoked state.
 */
export function useRevokeSessionMutation() {
  const queryClient = useQueryClient();
  return useMutation<UserSessionDto, Error, string>({
    mutationFn: (sessionId) => sessionsApi.revoke(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.all });
    },
  });
}

/**
 * TanStack Query mutation hook for revoking every other active session
 * (i.e. all devices except the one currently in use). Invalidates the
 * sessions list on success.
 */
export function useRevokeOtherSessionsMutation() {
  const queryClient = useQueryClient();
  return useMutation<{ revokedCount: number }, Error, void>({
    mutationFn: () => sessionsApi.revokeOthers(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.all });
    },
  });
}
